import React from 'react';
import { Heart, Phone, Mail, MapPin } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Services', href: '#services' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    'Airport Transfer',
    'Temporary Housing',
    'Pet Care Services',
    'Moving Assistance',
    'House Cleaning',
    'Interior Decoration',
    'Garden Maintenance'
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand Section */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="mb-4 sm:mb-6">
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2">Pattama</h3>
              <p className="text-primary-300 font-medium text-sm sm:text-base">Real Estate Agent</p>
            </div>
            
            <p className="text-gray-300 mb-4 sm:mb-6 leading-relaxed text-sm sm:text-base">
              Your trusted partner for comprehensive real estate services in Chiang Mai. 
              Making your property journey seamless and stress-free.
            </p>

            <div className="flex items-center space-x-2 text-accent-300">
              <Heart size={16} className="fill-current" />
              <span className="text-sm">Serving Chiang Mai with dedication</span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold text-white mb-4 sm:mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.href}
                    className="text-gray-300 hover:text-primary-300 transition-colors duration-200 text-sm sm:text-base block py-1"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Services</h4>
            <ul className="space-y-3">
              {services.slice(0, 5).map((service, index) => (
                <li key={index}>
                  <span className="text-gray-300 text-sm">{service}</span>
                </li>
              ))}
              <li>
                <span className="text-gray-400 text-sm">+ More services</span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-6">Get in Touch</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Phone size={16} className="text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">+66 XX XXX XXXX</p>
                  <p className="text-gray-400 text-xs">Available 24/7</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Mail size={16} className="text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">pattama@realestate.com</p>
                  <p className="text-gray-400 text-xs">Quick response guaranteed</p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <MapPin size={16} className="text-primary-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-gray-300 text-sm">Chiang Mai, Thailand</p>
                  <p className="text-gray-400 text-xs">Serving all districts</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-gray-400 text-sm">
              © {currentYear} Pattama Real Estate Services. All rights reserved.
            </div>
            
            <div className="flex space-x-6 text-sm text-gray-400">
              <a href="/privacy" className="hover:text-primary-300 transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="/terms" className="hover:text-primary-300 transition-colors duration-200">
                Terms of Service
              </a>
            </div>
          </div>

          <div className="text-center mt-6 pt-6 border-t border-gray-800">
            <p className="text-gray-500 text-sm">
              Built with care for the Chiang Mai community • Professional real estate services since 2019
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 